import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { makeAjaxRequestBase } from "helpers/makeAjaxRequest";

import { esysDataServiceUrl, rootUrl, rosterUrl } from "stores/redux/actions/constants";
import { selectRosterOwnerLovList } from "./selectors";

export const apiSlice = createApi({
  reducerPath: "institution",
  baseQuery: fetchBaseQuery({ fetchFn: makeAjaxRequestBase, baseUrl: "" }),
  endpoints: (builder) => ({
    fetchAddressType: builder.query({
      query: (userGroup) => ({ url: `${rosterUrl}/owner/${userGroup}/addresstype/INSTITUTION`, method: "GET" })
    }),
    fetchInstitutionAffiliation: builder.query({
      query: (siteCtepId) => ({ url: `${rosterUrl}/institution/affiliation/${siteCtepId}`, method: "GET" })
    }),
    fetchCountryList: builder.query({
      query: () => ({ url: `${esysDataServiceUrl}/esys/countries`, method: "GET" }),
      transformResponse: (response) => {
        const data = response.map(c => ({ label: c.name, value: c.id, code: c.code }));

        // Move US and Canada to the top
        const usIndex = data.findIndex(item => item.code === "US");
        if (usIndex !== -1) {
          const usItem = data.splice(usIndex, 1)[0];
          data.unshift(usItem);
        }
        const caIndex = data.findIndex(item => item.code === "CA");
        if (caIndex !== -1) {
          const caItem = data.splice(caIndex, 1)[0];
          data.splice(1, 0, caItem);
        }
        return data;
      }
    }),
    fetchStateProvinces: builder.query({
      query: () => ({ url: `${esysDataServiceUrl}/esys/states`, method: "POST", body: {} }),
      transformResponse: (response) =>
        response?.data?.map(d => ({ label: d.stateName, value: d.stateCode, cnyId: d.cnyId }))
    }),
    fetchPersonRoleBrowse: builder.query({
      query: () => ({ url: `${rootUrl}/admin/personrolesetup/browse`, method: "POST", body: {} }),
      keepUnusedDataFor: 0,
      transformResponse: (response) => {
        const data = response.data || [];
        const d = data.map(x => ({ ...x, objectType: x.forObjectType ? x.forObjectType : "NULL" }));
        d.sort((a, b) => {
          if (a.Owner < b.Owner) return -1;
          if (a.Owner > b.Owner) return 1;
          if (a.RoleName < b.RoleName) return -1;
          if (a.RoleName > b.RoleName) return 1;
          return 0;
        });
        return d;
      }
    }),
    fetchPersonRegTypeLov: builder.query({
      query: () => ({ url: `${rootUrl}/admin/personrolesetup/registrationtypeenumlov?status=ACTIVE`, method: "GET" }),
      transformResponse: (response) =>
        response?.sort((a, b) => a.regTypeCode.localeCompare(b.regTypeCode))?.map(r => ({
          ...r,
          label: r.regTypeCode,
          value: r.regTypeCode
        }))
    }),
    fetchRosterOwnerLov: builder.query({
      query: () => ({ url: `${rootUrl}/lov/rosterowner`, method: "GET" }),
      skip: (_, api) => !!selectRosterOwnerLovList(api.getState()).length
    }),
    fetchProtocolLov: builder.query({
      query: () => ({ url: `${rootUrl}/lov/institprovidercredprotocol`, method: "GET" })
    }),
    fetchCredTypeLov: builder.query({
      query: () => ({ url: `${rootUrl}/lov/institprovidercredtype`, method: "GET" })
    }),
    fetchBrowserLeadOrgLov: builder.query({
      query: () => ({ url: `${rootUrl}/lov/institsiterestrictbrowserleadorg`, method: "GET" })
    }),

    fetcSiteCtepIdLov: builder.query({
      query: (siteObj) => ({
        url: `${rootUrl}/admin/managecommittee/${siteObj?.owner}/participants/${siteObj?.perId}/sitectepidlov`,
        method: "GET"
      })
    }),
    copypersons: builder.query({
      query: ({ ctepId, userGroup }) => ({
        url: `${rootUrl}/rosters/owner/${userGroup}/organizations/${ctepId}/copypersons`,
        method: "GET"
      }),
      transformResponse: (response, meta, arg) => {
        return response.find((d) => d.orgCtepId === arg.ctepId);
      },
      onError: (error) => {
        console.error("Error fetching copypersons:", error);
        return {};
      }
    })
  })
});
export const {
  useFetchAddressTypeQuery,
  useFetchInstitutionAffiliationQuery,
  useFetchCountryListQuery,
  useFetchStateProvincesQuery,
  useFetchPersonRoleBrowseQuery,
  useFetchPersonRegTypeLovQuery,
  useFetchRosterOwnerLovQuery,
  useFetchProtocolLovQuery,
  useFetchCredTypeLovQuery,
  useFetchBrowserLeadOrgLovQuery,
  useFetchSiteCtepIdLovQuery,
  useCopypersonsQuery
} = apiSlice;
